/**
 * Tailwind/CSS implementation of the Grid component.
 *
 * Dependency-free: renders a plain <div class="seeds-grid"> and drives layout
 * with a mix of inline styles and CSS custom properties consumed by grid.css.
 *
 * jsdom-safe rule (mirrors Icon/Avatar/Skeleton):
 * - Scalar values (a number of columns, or a scalar gap) are written as literal
 *   resolved inline styles (e.g. row-gap: 24px) so window.getComputedStyle sees
 *   them in tests. Do NOT use var(--space-N) here — jsdom cannot resolve it.
 * - Responsive values (an object) are emitted as inline CSS custom properties;
 *   the concrete property is set only by the media queries in grid.css so the
 *   overrides are not blocked by inline specificity.
 */

import React from "react";
import type { GapValue, GridProps } from "./GridTypes";

// Space scale → px, matching seeds-design-tokens/seeds-space/tokens.json and the
// mapping Box's styled-system applies on the styled-components path.
const SPACE_SCALE: Record<number, number> = {
  0: 0,
  100: 2,
  200: 4,
  300: 8,
  350: 12,
  400: 16,
  450: 24,
  500: 32,
  600: 40,
};

// Loose bag of CSS declarations + custom properties. Cast to React.CSSProperties
// at the JSX site (same approach as IconTailwind), since custom properties are
// not part of the CSSProperties type.
type GridStyle = Record<string, string>;

type ResponsiveGapObject = {
  sm?: GapValue;
  md?: GapValue;
  lg?: GapValue;
  xl?: GapValue;
};

type ResponsiveColumnsObject = {
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
};

// Resolve a scalar gap value to a concrete CSS length. Space-scale keys map to
// px; pixel/arbitrary strings ("20px", "1em") pass through unchanged.
const toGap = (value: GapValue): string =>
  typeof value === "number" ? `${SPACE_SCALE[value] ?? 0}px` : value;

const isResponsive = (value: unknown): boolean =>
  typeof value === "object" && value !== null;

// Set the responsive column custom properties, resolving the breakpoint cascade
// in JS (md falls back to sm, lg to md/sm, xl to lg/md/sm) exactly like
// getResponsiveStyles on the styled-components path.
const applyResponsiveColumns = (
  style: GridStyle,
  columns: ResponsiveColumnsObject
): void => {
  const { sm, md, lg, xl } = columns;
  style["--sg-cols"] = "repeat(1, 1fr)";
  style["--sg-cols-sm"] = `repeat(${sm ?? 1}, 1fr)`;
  style["--sg-cols-md"] = `repeat(${md ?? sm ?? 1}, 1fr)`;
  style["--sg-cols-lg"] = `repeat(${lg ?? md ?? sm ?? 1}, 1fr)`;
  style["--sg-cols-xl"] = `repeat(${xl ?? lg ?? md ?? sm ?? 1}, 1fr)`;
};

// Apply a single gap axis. Scalar → concrete inline property (jsdom-safe).
// Responsive → custom properties only, driven by grid.css media queries.
const applyGap = (
  style: GridStyle,
  axis: "row" | "column",
  value: GapValue | ResponsiveGapObject
): void => {
  const prefix = axis === "row" ? "--sg-row-gap" : "--sg-col-gap";

  if (isResponsive(value)) {
    const { sm, md, lg, xl } = value as ResponsiveGapObject;
    style[prefix] = "0";
    if (sm !== undefined) style[`${prefix}-sm`] = toGap(sm);
    const mdVal = md ?? sm;
    if (mdVal !== undefined) style[`${prefix}-md`] = toGap(mdVal);
    const lgVal = lg ?? md ?? sm;
    if (lgVal !== undefined) style[`${prefix}-lg`] = toGap(lgVal);
    const xlVal = xl ?? lg ?? md ?? sm;
    if (xlVal !== undefined) style[`${prefix}-xl`] = toGap(xlVal);
    return;
  }

  const resolved = toGap(value as GapValue);
  if (axis === "row") {
    style.rowGap = resolved;
  } else {
    style.columnGap = resolved;
  }
};

export const GridTailwind: React.FC<GridProps> = ({
  columns = 1,
  gap = 400,
  rowGap,
  columnGap,
  children,
  className,
}) => {
  const style: GridStyle = { display: "grid" };

  if (isResponsive(columns)) {
    applyResponsiveColumns(style, columns as ResponsiveColumnsObject);
  } else {
    style.gridTemplateColumns = `repeat(${columns as number}, 1fr)`;
  }

  applyGap(style, "row", rowGap ?? gap);
  applyGap(style, "column", columnGap ?? gap);

  const classes = ["seeds-grid", className].filter(Boolean).join(" ");

  return (
    <div className={classes} style={style as React.CSSProperties}>
      {children}
    </div>
  );
};

GridTailwind.displayName = "GridTailwind";
